<script lang="ts" setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { DropdownMenu, Icon } from "@/components/ui"
import { availableLanguages } from "@/i18n"
import { languageLabels } from "@/i18n/constants"

export type LanguageSelectorVariant = "default" | "compact"

interface LanguageSelectorProps {
  isTemplate?: boolean
  variant?: LanguageSelectorVariant
  isNavbar?: boolean
  activatorTabIndex?: string
  disableTeleport?: boolean
}

const props = withDefaults(defineProps<LanguageSelectorProps>(), {
  isTemplate: false,
  variant: "default",
  isNavbar: false,
  disableTeleport: false,
})

const { t, locale: currentLanguage } = useI18n()

function changeLanguage(selectedLanguage: string) {
  if (selectedLanguage === currentLanguage.value) {
    return
  }

  currentLanguage.value = selectedLanguage
  localStorage.setItem("currentLanguage", selectedLanguage)
  location.reload()
}

const menuItems = computed(() =>
  availableLanguages.map(locale => ({
    key: locale,
    label: languageLabels[locale] || locale,
    onClick: () => changeLanguage(locale),
    variant: (currentLanguage.value === locale ? "highlighted" : "primary") as "highlighted" | "primary",
    dismissOnInteract: true,
  })),
)
</script>

<template>
  <DropdownMenu :items="menuItems" :disable-teleport="disableTeleport">
    <template #activator="{ isOpen }">
      <button
        type="button"
        class="bg-none border-none cursor-pointer flex items-center gap-2 mt-2"
        :class="{
          'text-primary': !props.isNavbar && !isTemplate,
          'text-theme-link': !props.isNavbar && isTemplate,
        }"
        :tabindex="activatorTabIndex"
        data-test="language-selector-activator"
      >
        <template v-if="props.variant === 'compact'">
          <span class="inline-flex items-center gap-1">
            <Icon icon="Globe" size="sm" class="shrink-0" />
            {{ languageLabels[currentLanguage] || currentLanguage }}
          </span>
        </template>
        <template v-else>
          {{ t("language.language") }}: <strong>{{ languageLabels[currentLanguage] || currentLanguage }}</strong>
        </template>
        <Icon
          v-if="props.variant === 'default'"
          icon="CircleChevronDown"
          size="sm"
          class="gpx-menu-icon shrink-0 transition ease-linear transform rotate-0 -mr-1"
          :class="[{ 'rotate-180': isOpen }]"
        />
      </button>
    </template>
  </DropdownMenu>
</template>
